import { CartAddEvent } from '@theme/events';

const CART_TOAST_DURATION = 7000;

/**
 * @typedef {object} CardVolumeVariant
 * @property {number|string} id
 * @property {string} title
 * @property {string} optionLabel
 * @property {string} sprays
 * @property {boolean} available
 * @property {number} price
 * @property {string} priceFormatted
 * @property {number|null} compareAtPrice
 * @property {string} compareAtPriceFormatted
 * @property {string} url
 * @property {string} image
 */

class CardVolumeModal extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {CardVolumeVariant[]} */
  variants = [];

  /** @type {CardVolumeVariant | null} */
  selectedVariant = null;

  /** @type {number} */
  quantity = 1;

  /** @type {HTMLElement | null} */
  sourceFormComponent = null;

  /** @type {number | undefined} */
  toastTimeout = undefined;

  connectedCallback() {
    this.dialog = this.querySelector('[data-card-volume-dialog]');

    document.addEventListener('click', this.#handleProductCardAddClick, true);
    document.addEventListener('submit', this.#handleProductCardSubmit, true);

    this.querySelector('[data-card-volume-close]')?.addEventListener('click', this.#closeDialog);
    this.querySelector('[data-card-volume-options]')?.addEventListener('click', this.#handleVariantChange);
    this.querySelector('[data-card-volume-qty-minus]')?.addEventListener('click', this.#decreaseQuantity);
    this.querySelector('[data-card-volume-qty-plus]')?.addEventListener('click', this.#increaseQuantity);
    this.querySelector('[data-card-volume-submit]')?.addEventListener('click', this.#addSelectedVariantToCart);
    this.querySelector('[data-card-cart-toast-close]')?.addEventListener('click', this.#hideToast);
    this.querySelector('[data-card-cart-link]')?.addEventListener('click', this.#handleCartLinkClick);

    this.dialog?.addEventListener('click', this.#closeDialogOnBackdropClick);
    this.dialog?.addEventListener('cancel', this.#handleDialogCancel);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleProductCardAddClick, true);
    document.removeEventListener('submit', this.#handleProductCardSubmit, true);

    this.querySelector('[data-card-volume-close]')?.removeEventListener('click', this.#closeDialog);
    this.querySelector('[data-card-volume-options]')?.removeEventListener('click', this.#handleVariantChange);
    this.querySelector('[data-card-volume-qty-minus]')?.removeEventListener('click', this.#decreaseQuantity);
    this.querySelector('[data-card-volume-qty-plus]')?.removeEventListener('click', this.#increaseQuantity);
    this.querySelector('[data-card-volume-submit]')?.removeEventListener('click', this.#addSelectedVariantToCart);
    this.querySelector('[data-card-cart-toast-close]')?.removeEventListener('click', this.#hideToast);
    this.querySelector('[data-card-cart-link]')?.removeEventListener('click', this.#handleCartLinkClick);

    this.dialog?.removeEventListener('click', this.#closeDialogOnBackdropClick);
    this.dialog?.removeEventListener('cancel', this.#handleDialogCancel);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleProductCardAddClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const addButton = event.target.closest('product-form-component[data-card-volume-chooser="true"] button[name="add"]');
    if (!(addButton instanceof HTMLButtonElement) || addButton.disabled) return;

    const formComponent = addButton.closest('product-form-component[data-card-volume-chooser="true"]');
    if (!(formComponent instanceof HTMLElement)) return;
    if (formComponent.closest('[data-template*="product"]')) return;

    const variants = this.#getPurchasableVariants(formComponent);
    if (variants.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#open(formComponent, variants);
  };

  /**
   * @param {SubmitEvent} event
   */
  #handleProductCardSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    const formComponent = event.target.closest('product-form-component[data-card-volume-chooser="true"]');
    if (!(formComponent instanceof HTMLElement)) return;
    if (formComponent.closest('[data-template*="product"]')) return;

    const variants = this.#getPurchasableVariants(formComponent);
    if (variants.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#open(formComponent, variants);
  };

  /**
   * @param {HTMLElement} formComponent
   * @param {CardVolumeVariant[]} variants
   */
  #open(formComponent, variants) {
    this.sourceFormComponent = formComponent;
    this.variants = variants;
    this.selectedVariant = this.#getInitiallySelectedVariant(formComponent, variants);
    this.quantity = 1;

    this.#renderProductTitle(formComponent);
    this.#renderProductImage(formComponent);
    this.#renderVariantOptions();
    this.#renderSelectedVariant();
    this.#setError('');

    this.dialog?.showModal();
  }

  /**
   * @param {HTMLElement} formComponent
   */
  #renderProductTitle(formComponent) {
    const title = formComponent.dataset.cardVolumeProductTitle || '';
    const titleElement = this.querySelector('[data-card-volume-title]');

    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  /**
   * @param {HTMLElement} formComponent
   */
  #renderProductImage(formComponent) {
    const image = this.querySelector('[data-card-volume-image]');
    if (!(image instanceof HTMLImageElement)) return;

    const source = formComponent.dataset.cardVolumeProductImage || '';
    image.src = source;
    image.alt = formComponent.dataset.cardVolumeProductTitle || '';
    image.hidden = !source;
  }

  #renderVariantOptions() {
    const list = this.querySelector('[data-card-volume-options]');
    if (!(list instanceof HTMLElement)) return;

    list.innerHTML = '';

    for (const variant of this.variants) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'card-volume-modal__option';
      option.setAttribute('role', 'radio');
      option.dataset.cardVolumeOption = String(variant.id);

      const size = document.createElement('span');
      size.textContent = variant.title || variant.optionLabel;
      option.append(size);

      if (variant.sprays) {
        const sprays = document.createElement('span');
        sprays.className = 'card-volume-modal__option-sprays';
        sprays.textContent = variant.sprays;
        option.append(sprays);
      }

      list.append(option);
    }
  }

  #renderSelectedVariant() {
    const selectedVariant = this.selectedVariant;
    if (!selectedVariant) return;

    const priceElement = this.querySelector('[data-card-volume-price]');
    const quantityElement = this.querySelector('[data-card-volume-qty-value]');
    const minusButton = this.querySelector('[data-card-volume-qty-minus]');

    for (const option of this.querySelectorAll('[data-card-volume-option]')) {
      const isSelected = option.getAttribute('data-card-volume-option') === String(selectedVariant.id);
      option.setAttribute('aria-checked', String(isSelected));
    }

    if (selectedVariant.image) {
      const image = this.querySelector('[data-card-volume-image]');
      if (image instanceof HTMLImageElement) {
        image.src = selectedVariant.image;
        image.hidden = false;
      }
    }

    if (priceElement) {
      priceElement.textContent = selectedVariant.priceFormatted || this.#formatMoney(selectedVariant.price);
    }

    if (quantityElement) {
      quantityElement.textContent = String(this.quantity);
    }

    if (minusButton instanceof HTMLButtonElement) {
      minusButton.disabled = this.quantity <= 1;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleVariantChange = (event) => {
    if (!(event.target instanceof Element)) return;

    const option = event.target.closest('[data-card-volume-option]');
    if (!(option instanceof HTMLElement)) return;

    const nextVariant = this.variants.find(
      (variant) => String(variant.id) === option.getAttribute('data-card-volume-option')
    );
    if (!nextVariant) return;

    this.selectedVariant = nextVariant;
    this.#renderSelectedVariant();
  };

  #decreaseQuantity = () => {
    this.quantity = Math.max(1, this.quantity - 1);
    this.#renderSelectedVariant();
  };

  #increaseQuantity = () => {
    this.quantity += 1;
    this.#renderSelectedVariant();
  };

  #addSelectedVariantToCart = async () => {
    const selectedVariant = this.selectedVariant;
    const submitButton = this.querySelector('[data-card-volume-submit]');

    if (!selectedVariant || !(submitButton instanceof HTMLButtonElement)) return;

    // O rotulo e um span proprio: trocar o textContent do botao apagaria o icone.
    const submitLabel = this.querySelector('[data-card-volume-submit-label]') ?? submitButton;

    this.#setError('');
    submitButton.disabled = true;
    submitLabel.textContent = 'Adicionando...';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: this.#createCartFormData(selectedVariant),
      });
      const addResponse = await response.json();

      if (!response.ok || addResponse.status) {
        throw new Error(addResponse.description || addResponse.message || 'Nao foi possivel adicionar ao carrinho.');
      }

      const cart = await this.#fetchCart();

      document.dispatchEvent(
        new CartAddEvent(cart, String(selectedVariant.id), {
          source: 'card-volume-chooser',
          itemCount: cart.item_count,
          productId: this.sourceFormComponent?.dataset.productId,
          variantId: String(selectedVariant.id),
          sections: addResponse.sections,
        })
      );

      this.#closeDialog();
      this.#showToast(selectedVariant, cart);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Nao foi possivel adicionar ao carrinho.';
      this.#setError(message);
    } finally {
      submitButton.disabled = false;
      submitLabel.textContent = 'Adicionar ao carrinho';
    }
  };

  /**
   * @param {CardVolumeVariant} selectedVariant
   * @returns {FormData}
   */
  #createCartFormData(selectedVariant) {
    const formData = new FormData();
    formData.set('id', String(selectedVariant.id));
    formData.set('quantity', String(this.quantity));

    const sectionIds = this.#getCartSectionIds();
    if (sectionIds) {
      formData.set('sections', sectionIds);
      formData.set('sections_url', window.location.pathname);
    }

    return formData;
  }

  #getCartSectionIds() {
    const sectionIds = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    }

    return Array.from(sectionIds).join(',');
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Nao foi possivel atualizar o carrinho.');

    return response.json();
  }

  /**
   * @param {CardVolumeVariant} selectedVariant
   * @param {{ item_count: number }} cart
   */
  #showToast(selectedVariant, cart) {
    const toast = this.querySelector('[data-card-cart-toast]');
    if (!(toast instanceof HTMLElement)) return;

    const title = this.sourceFormComponent?.dataset.cardVolumeProductTitle || '';
    const productImage = selectedVariant.image || this.sourceFormComponent?.dataset.cardVolumeProductImage || '';
    const image = this.querySelector('[data-card-cart-image]');
    const titleElement = this.querySelector('[data-card-cart-title]');
    const priceElement = this.querySelector('[data-card-cart-price]');
    const countElement = this.querySelector('[data-card-cart-count]');
    const totalElement = this.querySelector('[data-card-cart-total]');
    const cartLink = this.querySelector('[data-card-cart-link]');

    if (image instanceof HTMLImageElement) {
      image.src = productImage;
      image.alt = title;
      image.hidden = !productImage;
    }

    if (titleElement) {
      titleElement.textContent = title;
    }

    if (priceElement) {
      priceElement.textContent = `${this.quantity}x${selectedVariant.priceFormatted || this.#formatMoney(selectedVariant.price)}`;
    }

    if (countElement) {
      countElement.textContent = `Total (${this.quantity} ${this.quantity === 1 ? 'produto' : 'produtos'}):`;
    }

    if (totalElement) {
      totalElement.textContent = this.#formatMoney(selectedVariant.price * this.quantity);
    }

    if (cartLink instanceof HTMLAnchorElement) {
      cartLink.href = Theme.routes.cart_url || '/cart';
    }

    toast.hidden = false;
    toast.dataset.cartCount = String(cart.item_count);

    if (this.toastTimeout) {
      window.clearTimeout(this.toastTimeout);
    }

    this.toastTimeout = window.setTimeout(this.#hideToast, CART_TOAST_DURATION);
  }

  #hideToast = () => {
    const toast = this.querySelector('[data-card-cart-toast]');
    if (toast instanceof HTMLElement) {
      toast.hidden = true;
    }

    if (this.toastTimeout) {
      window.clearTimeout(this.toastTimeout);
      this.toastTimeout = undefined;
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleCartLinkClick = (event) => {
    event.preventDefault();

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer instanceof HTMLElement && typeof /** @type {{ open?: () => void }} */ (cartDrawer).open === 'function') {
      /** @type {{ open: () => void }} */ (cartDrawer).open();
      this.#hideToast();
      return;
    }

    window.location.href = Theme.routes.cart_url || '/cart';
  };

  /**
   * @param {HTMLElement} formComponent
   * @returns {CardVolumeVariant[]}
   */
  #getVariantsFromFormComponent(formComponent) {
    const variantsScript = formComponent.querySelector('[data-card-volume-variants]');
    if (!(variantsScript instanceof HTMLScriptElement)) return [];

    try {
      const parsedVariants = JSON.parse(variantsScript.textContent || '[]');
      return Array.isArray(parsedVariants) ? parsedVariants : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * @param {HTMLElement} formComponent
   * @returns {CardVolumeVariant[]}
   */
  #getPurchasableVariants(formComponent) {
    return this.#getVariantsFromFormComponent(formComponent).filter((variant) => variant.available && variant.price > 0);
  }

  /**
   * @param {HTMLElement} formComponent
   * @param {CardVolumeVariant[]} variants
   * @returns {CardVolumeVariant | null}
   */
  #getInitiallySelectedVariant(formComponent, variants) {
    const selectedVariantInput = formComponent.querySelector('input[name="id"]');
    const selectedVariantId = selectedVariantInput instanceof HTMLInputElement ? selectedVariantInput.value : '';

    return variants.find((variant) => String(variant.id) === selectedVariantId) || variants[0] || null;
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const errorElement = this.querySelector('[data-card-volume-error]');
    if (!(errorElement instanceof HTMLElement)) return;

    errorElement.textContent = message;
    errorElement.hidden = message.length === 0;
  }

  #closeDialog = () => {
    this.dialog?.close();
  };

  /**
   * @param {MouseEvent} event
   */
  #closeDialogOnBackdropClick = (event) => {
    if (event.target === this.dialog) {
      this.#closeDialog();
    }
  };

  #handleDialogCancel = () => {
    this.#setError('');
  };

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(cents / 100);
  }
}

if (!customElements.get('card-volume-modal')) {
  customElements.define('card-volume-modal', CardVolumeModal);
}
